/**
 * Walk-In Guest Dialog
 * Direct check-in for guests without prior reservation
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { UserPlus, Loader2, CheckCircle, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';


export const WalkInDialog = ({
  open,
  onOpenChange,
  hotelId,
  onSuccess
}) => {
  const [step, setStep] = useState(1); // 1: Guest Info, 2: Room Selection, 3: Payment, 4: Success
  const [loading, setLoading] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [checkedInGuest, setCheckedInGuest] = useState(null);
  
  // Guest Information
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestAddress, setGuestAddress] = useState('');
  
  // Stay Information
  const [selectedRoomType, setSelectedRoomType] = useState('');
  const [selectedRoomNumber, setSelectedRoomNumber] = useState('');
  const [numberOfNights, setNumberOfNights] = useState(1);
  const [checkInDate, setCheckInDate] = useState('');
  
  // Payment Information
  const [paymentMethod, setPaymentMethod] = useState('');
  const [cardHolderName, setCardHolderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCVV, setCardCVV] = useState('');
  const [advancePayment, setAdvancePayment] = useState('');
  
  useEffect(() => {
    if (open) {
      // Set today as default check-in date
      const today = new Date().toISOString().split('T')[0];
      setCheckInDate(today);
    }
  }, [open]);
  
  useEffect(() => {
    if (open && hotelId && checkInDate) {
      loadAvailableRooms();
    }
  }, [open, hotelId, checkInDate]);
  
  useEffect(() => {
    if (selectedRoomType && availableRooms.length > 0) {
      const roomsOfType = availableRooms.filter(r => r.room_type_name === selectedRoomType);
      if (roomsOfType.length > 0 && !selectedRoomNumber) {
        setSelectedRoomNumber(roomsOfType[0].room_number);
      }
    }
  }, [selectedRoomType, availableRooms]);
  
  const loadAvailableRooms = async () => {
    try {
      setLoadingRooms(true);
      console.log('Loading rooms for hotel:', hotelId, 'date:', checkInDate);
      
      const response = await fetch(
        `${BACKEND_URL}/api/pms/${hotelId}/available-rooms?date=${checkInDate}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to load rooms');
      }
      
      const data = await response.json();
      console.log('Available rooms loaded:', data);
      
      setAvailableRooms(data.rooms || []);
      
      if (!data.rooms || data.rooms.length === 0) {
        toast.error('No rooms available for selected date');
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
      toast.error('Failed to load available rooms');
    } finally {
      setLoadingRooms(false);
    }
  };
  
  const handleNext = () => {
    // Validate guest info
    if (!guestName.trim()) {
      toast.error('Please enter guest name');
      return;
    }
    if (!guestEmail.trim()) {
      toast.error('Please enter guest email');
      return;
    }
    
    setStep(2);
  };
  
  const handleRoomSelection = () => {
    if (!selectedRoomType || !selectedRoomNumber) {
      toast.error('Please select room type and number');
      return;
    }
    if (!numberOfNights || numberOfNights < 1) {
      toast.error('Please enter valid number of nights');
      return;
    }
    
    // Move to payment step
    setStep(3);
  };
  
  const handleWalkInCheckIn = async () => {
    // Validate payment information
    if (!paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }
    
    if (paymentMethod === 'credit_card' || paymentMethod === 'debit_card') {
      if (!cardHolderName.trim()) {
        toast.error('Please enter card holder name');
        return;
      }
      if (!cardNumber.trim() || cardNumber.replace(/\s/g, '').length < 13) {
        toast.error('Please enter a valid card number');
        return;
      }
      if (!cardExpiry.trim()) {
        toast.error('Please enter card expiry date');
        return;
      }
      if (!cardCVV.trim() || cardCVV.length < 3) {
        toast.error('Please enter CVV');
        return;
      }
    }
    
    if (!advancePayment || parseFloat(advancePayment) <= 0) {
      toast.error('Please enter advance payment amount');
      return;
    }
    
    // Proceed with check-in
    if (!selectedRoomType || !selectedRoomNumber) {
      toast.error('Please select room type and number');
      return;
    }
    
    try {
      setLoading(true);
      
      // Calculate check-out date
      const checkIn = new Date(checkInDate);
      const checkOut = new Date(checkIn);
      checkOut.setDate(checkOut.getDate() + parseInt(numberOfNights));
      const checkOutDate = checkOut.toISOString().split('T')[0];
      
      // Find the selected room's ID
      const selectedRoom = availableRooms.find(r => r.room_number === selectedRoomNumber);
      if (!selectedRoom) {
        throw new Error('Selected room not found');
      }
      
      // Step 1: Create booking using standard endpoint
      const bookingResponse = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId,
          room_id: selectedRoom.id,
          guest_name: guestName,
          guest_email: guestEmail,
          guest_phone: guestPhone,
          check_in: checkInDate,
          check_out: checkOutDate,
          guests: 1,
          payment_method: paymentMethod,
          advance_payment: parseFloat(advancePayment),
          special_requests: `Walk-in guest. Address: ${guestAddress}`,
          is_walk_in: true
        })
      });
      
      if (!bookingResponse.ok) {
        const errorData = await bookingResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create booking');
      }
      
      const bookingData = await bookingResponse.json();
      console.log('✅ Step 2 Complete - Booking created:', bookingData);
      console.log('📋 Full booking object:', JSON.stringify(bookingData, null, 2));
      console.log('📋 bookingData.booking:', bookingData.booking);
      console.log('📋 bookingData.id:', bookingData.id);
      console.log('📋 Booking ID for check-in:', bookingData.booking?.id || bookingData.id);
      
      // Extract booking ID (handle both formats)
      const bookingId = bookingData.booking?.id || bookingData.id;
      console.log('📋 Extracted bookingId:', bookingId);
      console.log('📋 Type of bookingId:', typeof bookingId);
      
      if (!bookingId) {
        console.error('❌ No booking ID found in response:', bookingData);
        throw new Error('Booking created but ID not found in response');
      }
      
      // Step 3: Check in immediately
      console.log('📝 Step 3: Checking in guest...');
      console.log('Check-in params:', {
        booking_id: bookingId,
        room_number: selectedRoomNumber
      });
      
      let checkInResponse;
      try {
        checkInResponse = await fetch(`${BACKEND_URL}/api/pms/checkin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            booking_id: bookingId,
            room_number: selectedRoomNumber
          })
        });
      } catch (fetchError) {
        console.error('❌ Network error during check-in:', fetchError);
        throw new Error(`Network error during check-in: ${fetchError.message}`);
      }
      
      console.log('📝 Check-in response status:', checkInResponse.status);
      console.log('📝 Check-in response OK:', checkInResponse.ok);
      
      if (!checkInResponse.ok) {
        const errorData = await checkInResponse.json().catch(() => ({}));
        const errorMessage = errorData.error || errorData.detail || 'Check-in failed';
        console.error('❌ Check-in failed:', errorData);
        throw new Error(`Booking created but check-in failed: ${errorMessage}`);
      }
      
      const checkInData = await checkInResponse.json();
      console.log('✅ Step 3 Complete - Check-in completed:', checkInData);
      console.log('🎉 Walk-in process complete!');
      
      // Verify check-in actually worked
      if (checkInData.success) {
        console.log('✅ Backend confirmed check-in success');
      } else {
        console.error('❌ Backend check-in failed:', checkInData);
        throw new Error(checkInData.error || 'Check-in returned failure');
      }
      
      // Store checked-in guest data
      setCheckedInGuest({
        guest_name: guestName,
        guest_email: guestEmail,
        guest_phone: guestPhone,
        guest_address: guestAddress,
        room_type: selectedRoomType,
        assigned_room_number: selectedRoomNumber,
        check_in: checkInDate,
        check_out: checkOutDate,
        nights: parseInt(numberOfNights),
        payment_method: paymentMethod,
        advance_payment: advancePayment
      });
      
      setStep(4); // Move to success step (step 4 now)
      toast.success('✅ Walk-in guest checked in successfully!');
      
      // Call onSuccess to refresh parent dashboard
      if (onSuccess) {
        onSuccess({
          guestName: guestName,
          roomNumber: selectedRoomNumber,
          timestamp: new Date().toISOString(),
          refresh: true // Signal to refresh the in-house list
        });
      }
    } catch (error) {
      console.error('❌ Walk-in check-in error:', error);
      toast.error(error.message || 'Failed to check in walk-in guest');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePrintRegCard = () => {
    const printWindow = window.open('', '_blank');
    const regCardHTML = generateRegCardHTML();
    printWindow.document.write(regCardHTML);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };
  
  const handleClose = () => {
    setStep(1);
    setGuestName('');
    setGuestEmail('');
    setGuestPhone('');
    setGuestAddress('');
    setSelectedRoomType('');
    setSelectedRoomNumber('');
    setNumberOfNights(1);
    setCheckedInGuest(null);
    onOpenChange(false);
  };
  
  const generateRegCardHTML = () => {
    const today = new Date().toLocaleDateString();
    const guest = checkedInGuest;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Registration Card - ${guest?.guest_name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', system-ui, sans-serif; padding: 30px; background: #f5f5f5; }
          .card-wrapper { max-width: 850px; margin: 0 auto; background: white; box-shadow: 0 10px 40px rgba(0,0,0,0.1); }
          .header { background: linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%); color: white; padding: 40px; text-align: center; }
          .hotel-logo { width: 80px; height: 80px; margin: 0 auto 15px; background: white; border-radius: 12px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
          .hotel-logo-placeholder { font-size: 32px; color: #0ea5e9; font-weight: 700; }
          .hotel-name { font-size: 32px; font-weight: 700; letter-spacing: 1px; margin-bottom: 8px; }
          .card-title { font-size: 14px; font-weight: 500; letter-spacing: 3px; text-transform: uppercase; opacity: 0.95; }
          .content { padding: 40px; }
          .section { margin-bottom: 35px; }
          .section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; color: #0ea5e9; letter-spacing: 1.5px; margin-bottom: 20px; padding-bottom: 8px; border-bottom: 2px solid #e0f2fe; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
          .info-item { padding: 12px 0; border-bottom: 1px solid #e5e7eb; }
          .info-item-full { padding: 12px 0; border-bottom: 1px solid #e5e7eb; grid-column: 1 / -1; }
          .info-label { font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
          .info-value { font-size: 16px; font-weight: 600; color: #1f2937; }
          .room-badge { display: inline-block; background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; padding: 8px 20px; border-radius: 6px; font-size: 24px; font-weight: 700; letter-spacing: 1px; }
          .walkin-badge { display: inline-block; background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white; padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; letter-spacing: 1px; margin-left: 10px; }
          .terms { background: #f9fafb; padding: 20px; border-radius: 8px; margin-top: 30px; border-left: 4px solid #0ea5e9; }
          .terms-title { font-size: 11px; font-weight: 700; color: #374151; margin-bottom: 10px; }
          .terms-text { font-size: 10px; color: #6b7280; line-height: 1.6; }
          .signature-section { margin-top: 50px; padding-top: 30px; border-top: 2px solid #e5e7eb; }
          .sig-label { font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 15px; }
          .sig-line { border-bottom: 2px solid #1f2937; width: 400px; margin: 20px 0; }
          .sig-note { font-size: 11px; color: #9ca3af; font-style: italic; }
          .footer { background: #f9fafb; padding: 25px 40px; text-align: center; border-top: 1px solid #e5e7eb; }
          .footer-text { font-size: 11px; color: #6b7280; line-height: 1.8; }
          @media print { body { background: white; padding: 0; } .card-wrapper { box-shadow: none; } }
        </style>
      </head>
      <body>
        <div class="card-wrapper">
          <div class="header">
            <div class="hotel-logo"><div class="hotel-logo-placeholder">🏨</div></div>
            <div class="hotel-name">The Grand Marina Hotel</div>
            <div class="card-title">Guest Registration Card <span class="walkin-badge">WALK-IN</span></div>
          </div>
          <div class="content">
            <div class="section">
              <div class="section-title">Guest Information</div>
              <div class="info-grid">
                <div class="info-item"><div class="info-label">Full Name</div><div class="info-value">${guest?.guest_name || ''}</div></div>
                <div class="info-item"><div class="info-label">Email Address</div><div class="info-value">${guest?.guest_email || ''}</div></div>
                <div class="info-item"><div class="info-label">Phone Number</div><div class="info-value">${guest?.guest_phone || 'Not provided'}</div></div>
                <div class="info-item"><div class="info-label">Registration Date</div><div class="info-value">${today}</div></div>
                <div class="info-item-full"><div class="info-label">Address</div><div class="info-value">${guest?.guest_address || 'Not provided'}</div></div>
              </div>
            </div>
            <div class="section">
              <div class="section-title">Accommodation Details</div>
              <div class="info-grid">
                <div class="info-item"><div class="info-label">Room Number</div><div class="info-value"><span class="room-badge">${guest?.assigned_room_number || ''}</span></div></div>
                <div class="info-item"><div class="info-label">Room Type</div><div class="info-value">${guest?.room_type || ''}</div></div>
                <div class="info-item"><div class="info-label">Check-In Date</div><div class="info-value">${guest?.check_in || ''}</div></div>
                <div class="info-item"><div class="info-label">Check-Out Date</div><div class="info-value">${guest?.check_out || ''}</div></div>
                <div class="info-item"><div class="info-label">Duration of Stay</div><div class="info-value">${guest?.nights || ''} Nights</div></div>
              </div>
            </div>
            <div class="terms">
              <div class="terms-title">Terms & Conditions</div>
              <div class="terms-text">By signing below, I acknowledge that I have read and agree to the hotel's terms and conditions. I understand that I am responsible for all charges incurred during my stay, including room charges, taxes, and any additional services or amenities used.</div>
            </div>
            <div class="signature-section">
              <div class="sig-label">Guest Signature</div>
              <div class="sig-line"></div>
              <div class="sig-note">I agree to the terms and conditions of my stay</div>
            </div>
          </div>
          <div class="footer">
            <div class="footer-text"><strong>The Grand Marina Hotel</strong><br>Dubai, UAE | contact@grandmarina.com | +971 4 XXX XXXX<br>Thank you for choosing The Grand Marina Hotel. We wish you a pleasant stay!</div>
          </div>
        </div>
      </body>
      </html>
    `;
  };
  
  const roomTypes = [...new Set(availableRooms.map(r => r.room_type_name))];
  const roomNumbers = availableRooms
    .filter(r => r.room_type_name === selectedRoomType)
    .map(r => r.room_number);
  
  console.log('Room types available:', roomTypes);
  console.log('Available rooms count:', availableRooms.length);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="walk-in-dialog">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-2xl font-bold">Walk-In Guest Check-In</DialogTitle>
          <p className="text-sm text-gray-500 mt-2">
            Directly check in a walk-in guest without creating a reservation first.
          </p>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="guest-name" className="text-sm font-semibold">Guest Name *</Label>
                <Input
                  id="guest-name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="John Doe"
                  className="h-12"
                  data-testid="guest-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="guest-email">Email Address *</Label>
                <Input
                  id="guest-email"
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="john@example.com"
                  data-testid="guest-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="guest-phone" className="text-sm font-semibold">Phone Number</Label>
                <Input
                  id="guest-phone"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="h-12"
                  data-testid="guest-phone-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="checkin-date" className="text-sm font-semibold">Check-In Date</Label>
                <Input
                  id="checkin-date"
                  type="date"
                  value={checkInDate}
                  onChange={(e) => setCheckInDate(e.target.value)}
                  className="h-12"
                  data-testid="checkin-date-input"
                />
              </div>
              
              <div className="space-y-2 col-span-2">
                <Label htmlFor="guest-address" className="text-sm font-semibold">Address</Label>
                <Input
                  id="guest-address"
                  value={guestAddress}
                  onChange={(e) => setGuestAddress(e.target.value)}
                  placeholder="123 Main St, City, Country"
                  className="h-12"
                  data-testid="guest-address-input"
                />
              </div>
            </div>

            <DialogFooter className="gap-3 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="h-12 px-8">
                Cancel
              </Button>
              <Button 
                onClick={handleNext} 
                data-testid="next-step-btn"
                className="h-12 px-8 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold"
              >
                Next: Room Selection →
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="room-type">Room Type *</Label>
                <Select 
                  value={selectedRoomType} 
                  onValueChange={setSelectedRoomType}
                  disabled={loadingRooms || roomTypes.length === 0}
                >
                  <SelectTrigger data-testid="room-type-select">
                    <SelectValue placeholder={loadingRooms ? "Loading rooms..." : roomTypes.length === 0 ? "No rooms available" : "Select room type"} />
                  </SelectTrigger>
                  <SelectContent>
                    {roomTypes.length === 0 ? (
                      <SelectItem value="none" disabled>No rooms available</SelectItem>
                    ) : (
                      roomTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="room-number">Room Number *</Label>
                <Select 
                  value={selectedRoomNumber} 
                  onValueChange={setSelectedRoomNumber}
                  disabled={!selectedRoomType || loadingRooms}
                >
                  <SelectTrigger data-testid="room-number-select">
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    {roomNumbers.map((num) => (
                      <SelectItem key={num} value={num}>Room {num}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="nights">Number of Nights *</Label>
                <Input
                  id="nights"
                  type="number"
                  min="1"
                  value={numberOfNights}
                  onChange={(e) => setNumberOfNights(e.target.value)}
                  data-testid="nights-input"
                />
              </div>
            </div>

            <Separator />

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Guest Summary</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p><strong>Name:</strong> {guestName}</p>
                <p><strong>Email:</strong> {guestEmail}</p>
                <p><strong>Check-In:</strong> {checkInDate}</p>
                <p><strong>Nights:</strong> {numberOfNights}</p>
                {selectedRoomType && <p><strong>Room:</strong> {selectedRoomType} - Room {selectedRoomNumber}</p>}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>
                ← Back
              </Button>
              <Button 
                onClick={handleRoomSelection} 
                disabled={loading || !selectedRoomNumber}
                className="h-12 px-8 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold"
                data-testid="next-payment-btn"
              >
                Next: Payment →
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Payment Information</h3>
              <p className="text-sm text-gray-500 mb-6">Please collect payment information before completing check-in</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="payment-method" className="text-sm font-semibold">Payment Method *</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger id="payment-method" className="h-12">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="debit_card">Debit Card</SelectItem>
                    <SelectItem value="upi">UPI / Digital Payment</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(paymentMethod === 'credit_card' || paymentMethod === 'debit_card') && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="card-holder" className="text-sm font-semibold">Card Holder Name *</Label>
                    <Input
                      id="card-holder"
                      value={cardHolderName}
                      onChange={(e) => setCardHolderName(e.target.value)}
                      placeholder="John Doe"
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="card-number" className="text-sm font-semibold">Card Number *</Label>
                    <Input
                      id="card-number"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      placeholder="1234 5678 9012 3456"
                      maxLength="19"
                      className="h-12"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="card-expiry" className="text-sm font-semibold">Expiry (MM/YY) *</Label>
                      <Input
                        id="card-expiry"
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(e.target.value)}
                        placeholder="12/25"
                        maxLength="5"
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="card-cvv" className="text-sm font-semibold">CVV *</Label>
                      <Input
                        id="card-cvv"
                        type="password"
                        value={cardCVV}
                        onChange={(e) => setCardCVV(e.target.value)}
                        placeholder="123"
                        maxLength="4"
                        className="h-12"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="advance-payment" className="text-sm font-semibold">Advance Payment Amount *</Label>
                <Input
                  id="advance-payment"
                  type="number"
                  min="0"
                  step="0.01"
                  value={advancePayment}
                  onChange={(e) => setAdvancePayment(e.target.value)}
                  placeholder="0.00"
                  className="h-12"
                />
                <p className="text-xs text-gray-500">Enter the amount collected from guest</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Booking Summary</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p><strong>Guest:</strong> {guestName}</p>
                <p><strong>Room:</strong> {selectedRoomType} - Room {selectedRoomNumber}</p>
                <p><strong>Check-In:</strong> {checkInDate}</p>
                <p><strong>Nights:</strong> {numberOfNights}</p>
                <p><strong>Payment Method:</strong> {paymentMethod ? paymentMethod.replace('_', ' ').toUpperCase() : 'Not selected'}</p>
                {advancePayment && <p><strong>Advance Payment:</strong> ${parseFloat(advancePayment).toFixed(2)}</p>}
              </div>
            </div>

            <DialogFooter className="gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep(2)} className="h-12 px-8">
                ← Back
              </Button>
              <Button 
                onClick={handleWalkInCheckIn} 
                disabled={loading || !paymentMethod || !advancePayment}
                className="h-12 px-8 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-semibold"
                data-testid="confirm-walkin-btn"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : 'Complete Check-In'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6 py-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Check-In Successful!</h3>
              <p className="text-gray-600">
                <strong>{checkedInGuest?.guest_name}</strong> has been checked into Room {checkedInGuest?.assigned_room_number}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Guest:</span>
                <span className="font-semibold">{checkedInGuest?.guest_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Room:</span>
                <span className="font-semibold">{checkedInGuest?.room_type} - Room {checkedInGuest?.assigned_room_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Check-In:</span>
                <span className="font-semibold">{checkedInGuest?.check_in}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Check-Out:</span>
                <span className="font-semibold">{checkedInGuest?.check_out}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Nights:</span>
                <span className="font-semibold">{checkedInGuest?.nights}</span>
              </div>
            </div>

            <Button 
              onClick={handlePrintRegCard}
              variant="outline"
              className="w-full"
              data-testid="print-reg-card-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Registration Card
            </Button>

            <DialogFooter>
              <Button onClick={handleClose} className="w-full">
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
